import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import ButtonSubmit from "../../../Buttons/ButtonSubmit";
import { title } from "../../../Common/Title/Title";
import { eventTeam } from "../../../Common/EventRole/EventRole";
import { toast } from "react-toastify";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import "react-toastify/dist/ReactToastify.css";

function EditTeam({ 
  userID, 
  token, 
  eventID, 
  memberData, 
  setIsOffcanvasOpen,
  onSuccess 
}) {
  const queryClient = useQueryClient();
  
  const [inputs, setInputs] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load existing member data
  useEffect(() => {
    if (memberData) {
      setInputs({
        firstName: memberData.firstName || "",
        lastName: memberData.lastName || "",
        email: memberData.email || "",
        phoneNumber: memberData.phoneNumber || "",
        roles: memberData.role || memberData.roles || "",
        title: memberData.title || "",
      });
    }
  }, [memberData]);

  const validateForm = () => {
    const errors = {};
    if (!inputs.firstName?.trim()) errors.firstName = "First name is required";
    if (!inputs.lastName?.trim()) errors.lastName = "Last name is required";
    if (!inputs.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(inputs.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!inputs.roles?.trim()) errors.roles = "Role is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    const memberId = memberData?._id || memberData?.id;

    if (!memberId) {
      console.error("❌ Member ID is missing from memberData:", memberData);
      setSubmitError("Cannot update: Member ID is missing. Please refresh and try again.");
      setIsLoading(false);
      return;
    }

    const url = `${process.env.REACT_APP_API}events/update-team-member/${eventID}`;

    const payload = {
      memberId: memberId,
      firstName: inputs.firstName?.trim(),
      lastName: inputs.lastName?.trim(),
      email: inputs.email?.trim(),
      phoneNumber: inputs.phoneNumber?.trim(),
      roles: inputs.roles?.trim(),
    };

    if (inputs.title) payload.title = inputs.title;

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success("Team member updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });

        setSubmitSuccess("Team member updated successfully!");
        setSubmitError(null);
        
        // Invalidate queries to refresh the list
        await queryClient.invalidateQueries({ queryKey: ['teamMembers', eventID] });
        await queryClient.invalidateQueries({ queryKey: ['eventTeam', eventID] });
        await queryClient.invalidateQueries({ queryKey: ['organizerTeam', eventID] });
        
        setTimeout(() => {
          setIsOffcanvasOpen(false);
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess();
          }
        }, 1000);
      } else {
        const errorMsg = responseData?.message || responseData?.data || "Failed to update team member.";
        setSubmitError(errorMsg);
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("❌ Network Error:", error);
      const errorMsg = "Network error. Please check your connection and try again.";
      setSubmitError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100 d-flex justify-content-center p-3">
      <form noValidate autoComplete="off" data-testid="form" onSubmit={handleSubmit}>
        <Row>
          {/* Removed ProfilePicture section */}

          {submitSuccess && (
            <Col md={12}>
              <div className="alert alert-success mb-3" style={{ fontSize: "14px" }}>
                {submitSuccess}
              </div>
            </Col>
          )}

          {submitError && (
            <Col md={12}>
              <div className="alert alert-danger mb-3" style={{ fontSize: "14px" }}>
                {submitError}
              </div>
            </Col>
          )}

          <Col md={6} lg={6}>
            <label>Role <span className="text-danger">*</span></label>
            <select
              name="roles"
              value={inputs.roles}
              onChange={handleChange}
              className={`mb-3 form-control ${formErrors.roles ? "is-invalid" : ""}`}
              required
              disabled={isLoading}
            >
              <option value="">Select...</option>
              {eventTeam?.map((team, i) => (
                <option key={i} value={team.role}>{team.name}</option>
              ))}
            </select>
            {formErrors.roles && <div className="invalid-feedback d-block">{formErrors.roles}</div>}
          </Col>

          <Col md={6} lg={6}>
            <label>Title</label>
            <select 
              name="title" 
              value={inputs.title} 
              onChange={handleChange} 
              className="mb-3 form-control"
              disabled={isLoading}
            >
              <option value="">Select...</option>
              {title?.map((t, i) => <option key={i} value={t}>{t}</option>)}
            </select>
          </Col>

          <Col md={6} lg={6}>
            <label>First name <span className="text-danger">*</span></label>
            <input 
              type="text" 
              name="firstName" 
              value={inputs?.firstName} 
              placeholder="Enter First name"
              onChange={handleChange} 
              className={`mb-3 form-control fromInput ${formErrors.firstName ? "is-invalid" : ""}`} 
              required 
              disabled={isLoading}
            />
            {formErrors.firstName && <div className="invalid-feedback d-block">{formErrors.firstName}</div>}
          </Col>

          <Col md={6} lg={6}>
            <label>Last name <span className="text-danger">*</span></label>
            <input 
              type="text" 
              name="lastName" 
              value={inputs?.lastName} 
              placeholder="Enter Last name"
              onChange={handleChange} 
              className={`mb-3 form-control fromInput ${formErrors.lastName ? "is-invalid" : ""}`} 
              required 
              disabled={isLoading}
            />
            {formErrors.lastName && <div className="invalid-feedback d-block">{formErrors.lastName}</div>}
          </Col>

          <Col md={6} lg={6}>
            <label>Email <span className="text-danger">*</span></label>
            <input 
              type="email" 
              name="email" 
              value={inputs?.email} 
              placeholder="Enter email"
              onChange={handleChange} 
              className={`mb-3 form-control fromInput ${formErrors.email ? "is-invalid" : ""}`} 
              required 
              disabled={isLoading}
            />
            {formErrors.email && <div className="invalid-feedback d-block">{formErrors.email}</div>}
          </Col>

          <Col md={6} lg={6}>
            <label>Phone number</label>
            <div className="phone-input-container">
              <PhoneInput 
                international 
                countryCallingCodeEditable={false} 
                defaultCountry="ZA"
                value={inputs.phoneNumber ? inputs.phoneNumber : null}
                onChange={(value) => setInputs({ ...inputs, phoneNumber: value })}
                className="custom-phone-input"
                disabled={isLoading}
              />
            </div>
          </Col>

          <div className="mb-3"></div>
          
          {/* Button Section */}
          <div className="w-100 d-flex justify-content-center gap-3">
            <button
              type="button"
              onClick={() => setIsOffcanvasOpen(false)}
              disabled={isLoading}
              className="btn btn-outline-secondary px-4"
              style={{
                borderRadius: "25px",
                fontWeight: "500",
                minWidth: "120px",
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.3s ease"
              }}
            >
              {isLoading ? "Please wait..." : "Cancel"}
            </button>

            <ButtonSubmit 
              Title={isLoading ? "Updating..." : "Update Member"} 
              ColorText="white"
              BorderColor="#F68A20" 
              BackgroundColor={isLoading ? "#ccc" : "#F68A20"}
              borderRadius="25px" 
              handleOnclick={handleSubmit}
              pending={isLoading} 
              type="submit"
              disabled={isLoading}
            />
          </div>
        </Row>
      </form>
    </div>
  );
}

export default EditTeam;